import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Gift, X, ArrowRight } from "lucide-react";
import { track } from "../lib/api";

/* Gentle, dismissible site-wide reminder pointing buyers to the £50 survey
   and conveyancing rewards. Calm and on-brand — no timers, no "limited time"
   language. Dismiss is permanent so visitors are never nagged again. */

// SECURITY NOTE: localStorage stores only a single non-sensitive flag ("1")
// marking that the visitor has already seen (or engaged with) this reminder.
// No personal data, tokens or identifiers — reading it via XSS has no value.
// The nudge pops up AT MOST ONCE per visitor: the flag is set the moment it is
// shown, dismissed, clicked, or the visitor enters the reward funnel.
const SEEN_KEY = "mp-rewards-nudge";
const COOKIE_KEY = "mp-cookie-consent";
const SHOW_DELAY_MS = 6000;
const hasSeen = () => typeof window !== "undefined" && localStorage.getItem(SEEN_KEY) === "1";
const markSeen = () => { try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* storage unavailable */ } };

// Pages where the reminder would be redundant (already there) or intrusive.
const HIDDEN = ["/rewards", "/survey-quote", "/conveyancing-quote", "/reward-terms", "/survey-reward-terms", "/cashback"];
const isHidden = (path) => HIDDEN.some((p) => path === p || path.startsWith(`${p}/`));

const RewardsNudge = () => {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [engaged, setEngaged] = useState(false);

  // Entering the reward funnel (rewards hub / quote / terms / cashback) means
  // the visitor has already started — suppress the nudge now and for good.
  useEffect(() => {
    if (isHidden(location.pathname)) { markSeen(); setEngaged(true); }
  }, [location.pathname]);

  useEffect(() => {
    if (hasSeen()) return; // already shown/dismissed/engaged once — never again
    let waited = 0;
    const timer = setInterval(() => {
      if (hasSeen()) { clearInterval(timer); return; } // entered funnel meanwhile
      waited += 1000;
      const cookieResolved = !!localStorage.getItem(COOKIE_KEY);
      // Wait until the cookie choice is made so we never stack on the cookie
      // banner — but give up waiting after 20s and show anyway.
      if (waited >= SHOW_DELAY_MS && (cookieResolved || waited >= 20000)) {
        clearInterval(timer);
        markSeen(); // pops up at most once, ever
        setShow(true);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!show) return;
    track("rewards_nudge_view", {});
    const t = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(t);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    markSeen();
    track("rewards_nudge_dismiss", {});
    setMounted(false);
    setTimeout(() => setShow(false), 250);
  };

  if (!show || engaged || isHidden(location.pathname)) return null;

  return (
    <div className="fixed z-40 left-4 bottom-4 sm:left-6 sm:bottom-6 w-[calc(100vw-2rem)] max-w-[21rem] pointer-events-none" data-testid="rewards-nudge">
      <div
        className={`relative pointer-events-auto rounded-2xl border border-[#0F766E]/20 bg-white shadow-[0_12px_40px_rgba(11,18,32,0.16)] p-4 transition-[opacity,transform] duration-300 ease-out ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      >
        <button
          onClick={dismiss}
          aria-label="Dismiss rewards reminder"
          data-testid="rewards-nudge-dismiss"
          className="absolute top-2.5 right-2.5 w-7 h-7 grid place-items-center rounded-full text-[#94A3B8] hover:text-[#0B1220] hover:bg-[#F7FAF9] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <span className="w-9 h-9 shrink-0 rounded-lg bg-[#0F766E]/10 border border-[#0F766E]/20 grid place-items-center" aria-hidden="true">
            <Gift className="w-4.5 h-4.5 text-[#0F766E]" />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#0F766E]">MortgagePath Rewards</div>
            <p className="mt-1 text-[13px] text-[#0B1220] leading-snug">
              Earn up to <strong className="text-[#0B5F58]">£100 cashback</strong> — £50 after a qualifying property survey and £50 after qualifying conveyancing.
            </p>
            <Link
              to="/rewards?source=nudge"
              onClick={() => { markSeen(); track("rewards_nudge_click", { source: "nudge" }); }}
              data-testid="rewards-nudge-cta"
              className="mt-2.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0F766E] hover:text-[#0B5F58] transition-colors group"
            >
              See rewards <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
            </Link>
            <p className="mt-1.5 text-[11px] text-[#94A3B8] leading-snug">Eligibility &amp; reward terms apply.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsNudge;
